import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo
} from "react";
import {
  Grid,
  Button,
  Tooltip,
  Box,
  Stack,
  Paper,
  Typography
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DownloadIcon from "@mui/icons-material/Download";

import Card from "../../Components/Card";
import ResourceTable, { IGenerateColumns } from "../../Components/ResourceTable";
import { estruturaMercadologica, estruturaMercadologicaRegistros } from "../../Requests";
import useGlobalContext from "../../Hooks/useGlobalContext";
import getInitialDataFilter from "../../Components/PageFilters/initialDataFilter";

import PreserveEstruturaMercadologica from "./PreserveEstruturaMercadologica";
import RemoveEstruturaMercadologica from "./RemoveEstruturaMercadologica";
import EstruturaMercadologicaPageFilter from "../../Components/PageFilters/EstruturaMercadologicaPageFilter";
import SpedStatusHandler from "../Dashboard/SpedStatusHandler";

const EstruturaMercadologica: React.FC = () => {
  const { setFilters, filters, setLoading: setLoadingGlobal } = useGlobalContext();

  const [rechargeRequest, setRechargeRequest] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [idRemove, setIdRemove] = useState(0);
  const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = useState<number | null>(null);

  useEffect(() => {
    setFilters?.(getInitialDataFilter());
  }, []);

  const estruturaFilters = useMemo(
    () => ({
      page: 1,
      recordsPerPage: 15,
      ...filters
    }),
    [filters]
  );

  const reloadResourceTable = () => setRechargeRequest((n) => n + 1);

  const handleDelete = (setLoading: Dispatch<SetStateAction<boolean>>) => async (id: number) => {
    setIdRemove(id);
  };

  const handleOpenImport = () => setOpenModal(true);

  const handleDownloadModelo = () => {
    const link = document.createElement('a');
    link.href = '/modeloImportacaoEstruturaMercadologica.csv';
    link.download = 'modeloImportacaoEstruturaMercadologica.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateColumns: IGenerateColumns = ({ setLoading }) => [
    { field: "competencia", headerName: "Competência", minWidth: 180, flex: 2 },
    { field: "username", headerName: "Importado Por", minWidth: 180, flex: 2 },
    { field: "created_at", headerName: "Data de Importação", minWidth: 180, flex: 2 },
    { field: "status", headerName: "Status da Importação", minWidth: 200, flex: 2 },
    {
      field: "acao_remover",
      headerName: "Remover",
      align: "center",
      headerAlign: "center",
      minWidth: 100,
      renderCell: (params: any) => {
        const status = params?.row?.status;
        if (status === "Realizando importação") return "---";

        return (
          <Tooltip title="Remover Estrutura" arrow>
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => handleDelete(setLoading)(params.row?.identifier || 0)}
              aria-label="Remover estrutura"
            >
              <DeleteForeverIcon sx={{ color: "#dc3545" }} />
            </Box>
          </Tooltip>
        );
      }
    }
  ];

  const generateColumnsRegistros: IGenerateColumns = () => [
    { field: "cod_plu", headerName: "Código PLU", minWidth: 100, flex: 2 },
    { field: "descricao_completa", headerName: "Descrição Completa", minWidth: 300, flex: 3 },
    { field: "descricao_secao", headerName: "Seção", minWidth: 180, flex: 2 },
    { field: "descricao_grupo", headerName: "Grupo", minWidth: 180, flex: 2 },
    { field: "descricao_sub_grupo", headerName: "Sub Grupo", minWidth: 180, flex: 2 }
  ];

  return (
    <Grid container spacing={0.5}>
      <SpedStatusHandler
        onSpedFiscalIdChange={(id) => setSpedFiscalIdSelecionado(id)}
      />

      <EstruturaMercadologicaPageFilter />

      <Grid item xs={12} container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              backgroundColor: "#f8f9fa", 
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Baixe aqui o modelo de Estrutura Mercadológica para importação
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadModelo}
              sx={{ borderColor: "#1c2e4a", color: "#1c2e4a", alignSelf: "flex-start" }}
              aria-label="Baixar modelo"
            >
              Download
            </Button>
          </Paper>
        </Grid>
        
        <Grid item xs={6} display="flex" justifyContent="flex-end">
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleOpenImport}
              sx={{ backgroundColor: "#1c2e4a", color: "#FFF" }}
              aria-label="Importar estrutura"
            >
              Importar
            </Button>

            {spedFiscalIdSelecionado && spedFiscalIdSelecionado > 0 && (
              <PreserveEstruturaMercadologica
                openModal={openModal}
                setOpenModal={setOpenModal}
                reloadResourceTable={reloadResourceTable}
                spedFiscalIdSelecionado={spedFiscalIdSelecionado}
              />
            )}
          </Stack>
        </Grid>
      </Grid>

      <Card
        titleDashboard="Listagem de Importações de arquivos da Estrutura Mercadológica"
        icon="CloudUploadIcon"
        paperConfig={{ minHeight: 154 }}
        marginTop="0"
        lineDividerHeader={false}
      >
        <ResourceTable
          request={estruturaMercadologica}
          generateColumns={generateColumns}
          pageSizeOptions={[15]}
          getRowId={(row) => row.identifier}
          minHeight={60}
          rechargeRequest={rechargeRequest}
          autoPageSize
          filterDefinitions={({ page, recordsPerPage }) => ({ page, recordsPerPage })}
        />
      </Card>

      {idRemove > 0 && (
        <RemoveEstruturaMercadologica
          idEstruturaMercadologica={idRemove}
          reloadResourceTable={reloadResourceTable}
          close={() => setIdRemove(0)}
        />
      )}

      <Card
        titleDashboard="Estrutura Mercadológica Importada"
        icon="CloudUploadIcon"
        paperConfig={{ minHeight: 154 }}
        marginTop="0"
        lineDividerHeader={false}
      >
        <ResourceTable
          request={estruturaMercadologicaRegistros}
          generateColumns={generateColumnsRegistros}
          pageSizeOptions={[15]}
          getRowId={(row) => row.identifier}
          minHeight={60}
          rechargeRequest={rechargeRequest}
          autoPageSize
          filterDefinitions={() => estruturaFilters}
        />
      </Card>
    </Grid>
  );
};

export default EstruturaMercadologica;
